import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  computed,
  inject,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { SalesCloserService } from '../../../services/sales-closer.service';
import { FileDropZoneComponent } from '../../../shared/components/file-drop-zone/file-drop-zone.component';
import type {
  PortfolioScoutProperty,
  SalesDocumentType,
  SalesValuationMethod,
} from '../../../models/sales-closer.models';

@Component({
  selector: 'app-sales-closer-page',
  imports: [CurrencyPipe, FileDropZoneComponent],
  templateUrl: './sales-closer-page.component.html',
  styleUrl: './sales-closer-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SalesCloserPageComponent {
  private readonly authService = inject(AuthService);
  private readonly salesCloserService = inject(SalesCloserService);
  private readonly router = inject(Router);

  protected readonly clientName = signal('');
  protected readonly repName = signal('');
  protected readonly repTitle = signal('');
  protected readonly repEmail = signal('');
  protected readonly documentType = signal<SalesDocumentType>('single_property');
  protected readonly feePct = signal<number>(25);
  protected readonly selectedFile = signal<File | null>(null);

  protected readonly scoutLoading = signal(false);
  protected readonly scoutResults = signal<PortfolioScoutProperty[] | null>(null);
  protected readonly methodSelections = signal<Record<number, SalesValuationMethod>>({});

  protected readonly generating = signal(false);
  protected readonly apiError = signal<string | null>(null);

  protected readonly isMultiProperty = computed(() => this.documentType() === 'multi_property');

  constructor() {
    void this.prefillRep();
  }

  protected setClientName(value: string): void {
    this.clientName.set(value);
  }

  protected setRepName(value: string): void {
    this.repName.set(value);
  }

  protected setRepTitle(value: string): void {
    this.repTitle.set(value);
  }

  protected setRepEmail(value: string): void {
    this.repEmail.set(value);
  }

  protected setFeePct(value: string): void {
    const parsed = Number(value);
    if (!isNaN(parsed)) {
      this.feePct.set(parsed);
    }
  }

  protected setDocumentType(value: string): void {
    this.documentType.set(value as SalesDocumentType);
    this.resetScoutState();
  }

  protected onFileSelected(file: File | null): void {
    this.selectedFile.set(file);
    this.resetScoutState();
  }

  protected isRepInfoValid(): boolean {
    return (
      this.clientName().trim().length > 0 &&
      this.repName().trim().length > 0 &&
      this.repTitle().trim().length > 0 &&
      this.repEmail().trim().length > 0 &&
      this.isFeePctValid()
    );
  }

  protected isFeePctValid(): boolean {
    const fee = this.feePct();
    return fee >= 0 && fee <= 100;
  }

  protected canPreview(): boolean {
    return this.isMultiProperty() && this.selectedFile() !== null && !this.scoutLoading();
  }

  protected canGenerate(): boolean {
    if (!this.isRepInfoValid() || !this.selectedFile() || this.generating()) return false;
    if (this.isMultiProperty()) return this.scoutResults() !== null;
    return true;
  }

  protected availableMethodsFor(property: PortfolioScoutProperty): SalesValuationMethod[] {
    const methods: SalesValuationMethod[] = [];
    if (property.income_value !== null) methods.push('income');
    if (property.sales_value !== null) methods.push('sales');
    return methods;
  }

  protected methodSelectionFor(propertyIndex: number): SalesValuationMethod | null {
    return this.methodSelections()[propertyIndex] ?? null;
  }

  protected setMethodSelection(propertyIndex: number, value: string): void {
    this.methodSelections.update((current) => ({
      ...current,
      [propertyIndex]: value as SalesValuationMethod,
    }));
  }

  protected async previewProperties(): Promise<void> {
    const file = this.selectedFile();
    if (!file) return;
    this.scoutLoading.set(true);
    this.apiError.set(null);
    try {
      const response = await firstValueFrom(this.salesCloserService.scoutPortfolio(file));
      this.scoutResults.set(response.properties);
      const defaults: Record<number, SalesValuationMethod> = {};
      for (const property of response.properties) {
        const available = this.availableMethodsFor(property);
        const preferred =
          property.recommended_method && available.includes(property.recommended_method)
            ? property.recommended_method
            : available[0];
        if (preferred) {
          defaults[property.property_index] = preferred;
        }
      }
      this.methodSelections.set(defaults);
    } catch (error) {
      this.apiError.set(
        await this.resolveErrorMessage(error, 'Could not read valuation options from this file.'),
      );
    } finally {
      this.scoutLoading.set(false);
    }
  }

  protected async generateDocument(): Promise<void> {
    const file = this.selectedFile();
    if (!file || !this.canGenerate()) return;
    this.generating.set(true);
    this.apiError.set(null);
    try {
      const methods = this.isMultiProperty()
        ? Object.entries(this.methodSelections()).map(([propertyIndex, method]) => ({
            property_index: Number(propertyIndex),
            method,
          }))
        : undefined;
      const blob = await firstValueFrom(
        this.salesCloserService.createCloserDocument({
          clientName: this.clientName(),
          repName: this.repName(),
          repTitle: this.repTitle(),
          repEmail: this.repEmail(),
          documentType: this.documentType(),
          file,
          feePct: this.feePct(),
          methods,
        }),
      );
      this.downloadBlob(blob, this.buildFilename());
      await this.router.navigate(['/sales']);
    } catch (error) {
      this.apiError.set(await this.resolveErrorMessage(error, 'Could not generate the closer document.'));
    } finally {
      this.generating.set(false);
    }
  }

  private async prefillRep(): Promise<void> {
    try {
      const response = await firstValueFrom(this.authService.getCurrentUser());
      this.repName.set(response.user.display_name);
      this.repEmail.set(response.user.email);
    } catch {
      // Auth lookup failed — leave the rep fields blank for manual entry.
    }
  }

  private resetScoutState(): void {
    this.scoutResults.set(null);
    this.methodSelections.set({});
    this.apiError.set(null);
  }

  private buildFilename(): string {
    const safeClient = this.sanitizeFilenamePart(this.clientName()) || 'Client';
    const suffix = this.isMultiProperty() ? 'Portfolio_Summary' : 'ROI_Analysis';
    return `PPTA_${safeClient}_${suffix}.pdf`;
  }

  private sanitizeFilenamePart(value: string): string {
    return value
      .trim()
      .split('')
      .map((ch) => (/[a-zA-Z0-9]/.test(ch) ? ch : '_'))
      .join('')
      .split('_')
      .filter((part) => part.length > 0)
      .join('_');
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private async resolveErrorMessage(error: unknown, fallback: string): Promise<string> {
    if (!(error instanceof HttpErrorResponse)) return fallback;
    const body: unknown = error.error;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text()) as { detail?: string };
        return parsed.detail ?? fallback;
      } catch {
        return fallback;
      }
    }
    const detail = (body as Record<string, unknown> | null)?.['detail'];
    return typeof detail === 'string' ? detail : fallback;
  }
}
